import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Optional,
  SimpleChanges,
  EventEmitter,
  Output,
  Component,
  OnChanges,
  Input,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import {
  ServicePost,
  ShiftDetail,
  ShiftRecurrence,
} from "../../api/generated/ModulesAPIClient";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Site } from "../../sites/site.models";
import { CoveragePreset } from "../guard.model";
import { openConfirmDialog } from "../../confirm-dialog/confirm-dialog.component";

@Component({
  selector: "guard-schedule-editor",
  templateUrl: "./guard-schedule-editor.component.html",
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuardScheduleEditorComponent implements OnChanges {
  @Output() submit = new EventEmitter<
    Array<{ siteId: number; shifts: ShiftDetail[]; specialAttention: boolean }>
  >();
  @Output() cancel = new EventEmitter<void>();
  @Output() postsAdded = new EventEmitter<ServicePost[]>();
  @Output() postsRemoved = new EventEmitter<number[]>();

  @Input() datasetId!: number;
  @Input() iteration!: number;
  @Input() sitesById: ReadonlyMap<number, Site> = new Map();
  @Input() availableSites: Site[] = [];
  @Input() availableSitesLoading = false;
  @Input() canModifyPosts = true;
  @Input() primarySiteId?: number;
  @Input() showSpecialAttention = true;
  @Input() showSchedulingFields = true;
  refreshTick = 0;

  @ViewChild("addSiteDialog") private addSiteDialogTpl!: TemplateRef<unknown>;

  private _initialPostsRef: ServicePost[] = [];
  @Input() set initialPosts(servicePosts: ServicePost[] | null | undefined) {
    const next = servicePosts ?? [];
    const prev = this._initialPostsRef;
    const changedRef = next !== prev;
    const changedLen = next.length !== prev.length;
    const changedSites =
      changedLen || next.some((n, i) => n?.siteId !== prev[i]?.siteId);

    if (prev.length === 0 || changedRef || changedSites) {
      this._initialPostsRef = [...next];
      this.rebuildFromInitial(this._initialPostsRef);
      this.cdr.markForCheck();
    }
  }
  get initialPosts(): ServicePost[] {
    return this._initialPostsRef;
  }

  readonly weekdays = [
    { label: "Sun", value: 0 },
    { label: "Mon", value: 1 },
    { label: "Tue", value: 2 },
    { label: "Wed", value: 3 },
    { label: "Thu", value: 4 },
    { label: "Fri", value: 5 },
    { label: "Sat", value: 6 },
  ];

  readonly weeks = [
    { label: "Every week", value: 1 },
    { label: "Every 2 weeks", value: 2 },
    { label: "Every 3 weeks", value: 3 },
    { label: "Every 4 weeks", value: 4 },
  ];

  form = this.fb.group({ servicePosts: this.fb.array<FormGroup>([]) });
  get postsFormArray(): FormArray<FormGroup> {
    return this.form.get("servicePosts") as FormArray<FormGroup>;
  }

  pendingSiteIds: number[] = [];
  private addSiteDialogRef?: MatDialogRef<unknown>;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    @Optional() public dialogRef?: MatDialogRef<GuardScheduleEditorComponent>,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["datasetId"] || changes["iteration"] || changes["sitesById"]) {
      this.cdr.markForCheck();
    }
    if (changes["showSpecialAttention"]) {
      this.applySpecialAttentionVisibility();
    }
  }

  private buildServicePostFormGroup(servicePost: ServicePost): FormGroup {
    const formGroup = this.fb.group({
      siteId: [servicePost.siteId, Validators.required],
      endMode: ["never"],
      endDate: [null],
      specialAttention: [!!servicePost.specialAttention],
      shiftDetails: this.fb.array<FormGroup>([]),
    });

    const shiftDetailsFormArray = formGroup.get(
      "shiftDetails",
    ) as FormArray<FormGroup>;
    const shifts = servicePost.shifts ?? [];

    if (shifts.length === 0) {
      shiftDetailsFormArray.push(this.blankWeeklyRow());
      return formGroup;
    }

    const weeklyMap = new Map<
      string,
      { start: string; end: string; weeklyInterval: number; days: number[] }
    >();
    const monthlyByDayMap = new Map<
      string,
      { start: string; end: string; weekOfMonth: number; days: Set<number> }
    >();

    for (const s of shifts) {
      const shiftRecurrence: any = s.recurrence || {};
      const start = this.fromApiTime(s.startTime);
      const end = this.fromApiTime(s.endTime);

      if (
        shiftRecurrence.$recurrence === "perStopPerDay" ||
        shiftRecurrence.$recurrence === "perStopPerWeek"
      ) {
        continue;
      }

      if (shiftRecurrence.$recurrence === "weekly") {
        const weeklyInterval = Number(shiftRecurrence.weeklyInterval) || 1;
        const key = `${start}|${end}|${weeklyInterval}`;
        if (!weeklyMap.has(key)) {
          weeklyMap.set(key, {
            start,
            end,
            weeklyInterval: weeklyInterval,
            days: [],
          });
        }
        weeklyMap.get(key)!.days.push(Number(shiftRecurrence.dayOfWeek));
        continue;
      }

      if (shiftRecurrence.$recurrence === "monthlyByDay") {
        const weekOfMonth = Number(shiftRecurrence.weekOfMonth) || 1;
        const key = `${start}|${end}|${weekOfMonth}`;
        if (!monthlyByDayMap.has(key)) {
          monthlyByDayMap.set(key, {
            start,
            end,
            weekOfMonth: weekOfMonth,
            days: new Set<number>(),
          });
        }
        monthlyByDayMap.get(key)!.days.add(Number(shiftRecurrence.dayOfWeek));
        continue;
      }

      // monthlyByDate: single row per dayOfMonth
      shiftDetailsFormArray.push(
        this.fb.group(
          {
            type: [{ value: "monthlyByDate", disabled: true }],
            dayOfMonth: [
              Number((shiftRecurrence as any).dayOfMonth) || 1,
              [Validators.min(1), Validators.max(31)],
            ],
            startTime: [start, Validators.required],
            endTime: [end, Validators.required],
          },
          { validators: [this.timeOrderValidator] },
        ),
      );
    }

    // Materialize grouped weekly rows
    for (const g of weeklyMap.values()) {
      for (const weekdays of this.splitWeeklyDaysByOccurrence(g.days)) {
        shiftDetailsFormArray.push(
          this.fb.group(
            {
              type: [{ value: "weekly", disabled: true }],
              weekdays: new FormControl<number[]>(weekdays, {
                nonNullable: true,
                validators: [Validators.required],
              }),
              weeklyInterval: [
                g.weeklyInterval,
                [Validators.min(1), Validators.max(4)],
              ],
              startTime: [g.start, Validators.required],
              endTime: [g.end, Validators.required],
            },
            { validators: [this.timeOrderValidator] },
          ),
        );
      }
    }

    // Materialize grouped monthlyByDay rows
    for (const g of monthlyByDayMap.values()) {
      shiftDetailsFormArray.push(
        this.fb.group(
          {
            type: [{ value: "monthlyByDay", disabled: true }],
            weekOfMonth: [
              g.weekOfMonth,
              [Validators.min(1), Validators.max(5)],
            ],
            weekdays: new FormControl<number[]>(
              Array.from(g.days).sort((a, b) => a - b),
              {
                nonNullable: true,
                validators: [Validators.required],
              },
            ),
            startTime: [g.start, Validators.required],
            endTime: [g.end, Validators.required],
          },
          { validators: [this.timeOrderValidator] },
        ),
      );
    }

    if (shiftDetailsFormArray.length === 0) {
      shiftDetailsFormArray.push(this.blankWeeklyRow());
    }

    return formGroup;
  }

  private rebuildFromInitial(posts: ServicePost[]): void {
    this.postsFormArray.clear();
    for (const servicePost of posts) {
      this.postsFormArray.push(this.buildServicePostFormGroup(servicePost));
    }
    this.applySpecialAttentionVisibility();
  }

  shiftDetailsFA(postIdx: number): FormArray<FormGroup> {
    return this.postsFormArray
      .at(postIdx)
      .get("shiftDetails") as FormArray<FormGroup>;
  }

  addWeeklyShiftDetail = (postIdx: number): void => {
    this.shiftDetailsFA(postIdx).push(this.blankWeeklyRow());
    this.cdr.markForCheck();
  };

  addMonthlyByDayShiftDetail = (postIdx: number): void => {
    this.shiftDetailsFA(postIdx).push(
      this.fb.group(
        {
          type: [{ value: "monthlyByDay", disabled: true }],
          weekOfMonth: [1, [Validators.min(1), Validators.max(5)]],
          weekdays: new FormControl<number[]>([], {
            nonNullable: true,
            validators: [Validators.required],
          }),
          startTime: ["", Validators.required],
          endTime: ["", Validators.required],
        },
        { validators: [this.timeOrderValidator] },
      ),
    );
    this.cdr.markForCheck();
  };

  addMonthlyByDateShiftDetail = (postIdx: number): void => {
    this.shiftDetailsFA(postIdx).push(
      this.fb.group(
        {
          type: [{ value: "monthlyByDate", disabled: true }],
          dayOfMonth: [1, [Validators.min(1), Validators.max(31)]],
          startTime: ["", Validators.required],
          endTime: ["", Validators.required],
        },
        { validators: [this.timeOrderValidator] },
      ),
    );
    this.cdr.markForCheck();
  };

  removeShiftDetail = (postIdx: number, rowIdx: number): void => {
    this.shiftDetailsFA(postIdx).removeAt(rowIdx);
    this.cdr.markForCheck();
  };

  private buildWeeklyRow(
    weekdays: number[],
    start: string,
    end: string,
    weeklyInterval = 1,
  ): FormGroup {
    return this.fb.group(
      {
        type: [{ value: "weekly", disabled: true }],
        weekdays: new FormControl<number[]>(
          [...weekdays].sort((a, b) => a - b),
          { nonNullable: true, validators: [Validators.required] },
        ),
        weeklyInterval: [
          weeklyInterval,
          [Validators.min(1), Validators.max(4)],
        ],
        startTime: [start, Validators.required],
        endTime: [end, Validators.required],
      },
      { validators: [this.timeOrderValidator] },
    );
  }

  private blankWeeklyRow(): FormGroup {
    return this.fb.group(
      {
        type: [{ value: "weekly", disabled: true }],
        weekdays: new FormControl<number[]>([], {
          nonNullable: true,
          validators: [Validators.required],
        }),
        weeklyInterval: [1, [Validators.min(1), Validators.max(4)]],
        startTime: ["", Validators.required],
        endTime: ["", Validators.required],
      },
      { validators: [this.timeOrderValidator] },
    );
  }

  private splitWeeklyDaysByOccurrence(days: number[]): number[][] {
    const counts = new Map<number, number>();
    for (const day of days) {
      counts.set(day, (counts.get(day) ?? 0) + 1);
    }

    const weekdays = Array.from(counts.keys()).sort((a, b) => a - b);
    const rowCount = Math.max(...counts.values());
    const rows: number[][] = [];

    for (let occurrence = 0; occurrence < rowCount; occurrence++) {
      rows.push(weekdays.filter((day) => (counts.get(day) ?? 0) > occurrence));
    }

    return rows;
  }

  onSave(): void {
    if (!this.canSave()) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }
    const payload = this.getEditedSchedules(null, null);
    this.submit.emit(payload);
    this.dialogRef?.close(payload);
  }

  onCancel(): void {
    this.cancel.emit();
    this.dialogRef?.close();
  }
  private touchAndRefresh(): void {
    this.form.markAllAsTouched();
    this.refreshTick++;
    this.cdr.markForCheck();
  }

  public canSave(touchOnFail = true): boolean {
    const fail = () => {
      if (touchOnFail) this.touchAndRefresh();
      return false as const;
    };

    const posts = this.form?.get("servicePosts") as FormArray<FormGroup> | null;
    if (!posts || posts.length === 0) return fail();
    if (!this.showSchedulingFields) return true;

    const inRange = (value: number, min: number, max: number) =>
      Number.isFinite(value) && value >= min && value <= max;

    for (const post of posts.controls) {
      if (!(post instanceof FormGroup)) return fail();

      const shiftDetailsFormArray = post.get(
        "shiftDetails",
      ) as FormArray<FormGroup> | null;
      if (!shiftDetailsFormArray || shiftDetailsFormArray.length === 0)
        return fail();

      for (const shiftDetailsFormGroup of shiftDetailsFormArray.controls) {
        if (!(shiftDetailsFormGroup instanceof FormGroup)) return fail();

        const raw = (shiftDetailsFormGroup.getRawValue?.() ?? {}) as {
          type?: "weekly" | "monthlyByDay" | "monthlyByDate";
          startTime?: string;
          endTime?: string;
        };
        const type = raw.type;
        const startTime = shiftDetailsFormGroup.get("startTime")?.value as
          | string
          | undefined;
        const endTime = shiftDetailsFormGroup.get("endTime")?.value as
          | string
          | undefined;
        if (!startTime || !endTime) return fail();

        const startTimeMinutes = this.toMinutes(startTime);
        const endTimeMinutes = this.toMinutes(endTime);
        if (
          startTimeMinutes == null ||
          endTimeMinutes == null ||
          startTimeMinutes >= endTimeMinutes
        )
          return fail();

        if (type === "weekly") {
          const weekdays: number[] =
            shiftDetailsFormGroup.get("weekdays")?.value ?? [];
          const weeklyInterval = Number(
            shiftDetailsFormGroup.get("weeklyInterval")?.value ?? 1,
          );
          if (!Array.isArray(weekdays) || weekdays.length === 0) return fail();
          if (!weeklyInterval || !inRange(weeklyInterval, 1, 4)) return fail();
          if (!weekdays.every((d) => inRange(d, 0, 6))) return fail();
          continue;
        }

        if (type === "monthlyByDay") {
          const weekOfMonth = Number(
            shiftDetailsFormGroup.get("weekOfMonth")?.value ?? 0,
          );
          const weekdays: number[] =
            shiftDetailsFormGroup.get("weekdays")?.value ?? [];
          if (![1, 2, 3, 4, 5].includes(weekOfMonth)) return fail();
          if (!Array.isArray(weekdays) || weekdays.length === 0) return fail();
          if (!weekdays.every((d) => inRange(d, 0, 6))) return fail();
          continue;
        }

        if (type === "monthlyByDate") {
          const dayOfMonth = Number(
            shiftDetailsFormGroup.get("dayOfMonth")?.value ?? 0,
          );
          if (!inRange(dayOfMonth, 1, 31)) return fail();
          continue;
        }

        return fail(); // unknown/missing type
      }
    }

    return true;
  }

  addPostForSite(siteId: number): void {
    const site = (this.availableSites ?? []).find((s) => s?.siteId === siteId);
    if (site && site.siteId != null && !this.sitesById?.has(site.siteId)) {
      const next = new Map(this.sitesById ?? new Map<number, Site>());
      next.set(site.siteId, site);
      this.sitesById = next;
    }
    this.postsFormArray.push(
      this.buildServicePostFormGroup({ siteId, shifts: [] } as ServicePost),
    );
    this.applySpecialAttentionVisibility();
    this.cdr.markForCheck();
  }

  confirmRemovePost = async (index: number): Promise<void> => {
    const post = this.postsFormArray.at(index);
    const siteId = post?.get("siteId")?.value as number | undefined;
    if (siteId != null && this.primarySiteId === siteId) {
      return;
    }
    const siteName =
      siteId != null ? this.sitesById?.get(siteId)?.siteName : null;
    const label =
      siteName || (siteId != null ? `Site #${siteId}` : "this site");

    const confirmed = await openConfirmDialog(
      this.dialog,
      `Remove ${label} from this service?`,
      "Remove Site",
    );
    if (!confirmed) return;

    this.postsFormArray.removeAt(index);
    if (siteId != null) {
      this.postsRemoved.emit([siteId]);
    }
    this.cdr.markForCheck();
  };

  getEditedSchedules(
    perStopRecurrence,
    perStopFrequency,
  ): Array<{
    siteId: number;
    shifts: ShiftDetail[];
    specialAttention: boolean;
  }> {
    const result: Array<{
      siteId: number;
      shifts: ShiftDetail[];
      specialAttention: boolean;
    }> = [];
    const perStop = perStopRecurrence != null;

    for (const post of this.postsFormArray.controls) {
      const siteId = post.get("siteId")!.value as number;
      const specialAttention = !!post.get("specialAttention")!.value;

      const rows = (post.get("shiftDetails") as FormArray<FormGroup>).controls;
      const shifts: ShiftDetail[] = [];

      for (const r of rows) {
        //const { type } = r.getRawValue() as { type: 'weekly' | 'monthlyByDay' | 'monthlyByDate' | 'perStopPerDay' | 'perStopPerWeek' };
        let type:
          | "weekly"
          | "monthlyByDay"
          | "monthlyByDate"
          | "perStopPerDay"
          | "perStopPerWeek";
        let start;
        let end;
        if (perStop) {
          //set type to correct value
          type = perStopRecurrence;
          start = this.toApiTime("00:00:00");
          end = this.toApiTime("23:59:00");
        } else {
          type = r.getRawValue().type;
          start = this.toApiTime(r.get("startTime")!.value);
          end = this.toApiTime(r.get("endTime")!.value);
        }

        if (type === "weekly") {
          const weekdays = (r.get("weekdays")!.value as number[]) || [];
          const weeklyInterval = Number(r.get("weeklyInterval")!.value) || 1;
          for (const dayOfWeek of weekdays) {
            shifts.push(
              ShiftDetail.fromJS({
                startTime: start,
                endTime: end,
                recurrence: ShiftRecurrence.fromJS({
                  $recurrence: "weekly",
                  dayOfWeek,
                  weeklyInterval: weeklyInterval,
                }),
              }),
            );
          }
          continue;
        }

        if (type === "monthlyByDay") {
          const weekOfMonth = Number(r.get("weekOfMonth")!.value);
          const weekdays = (r.get("weekdays")!.value as number[]) || [];
          for (const dayOfWeek of weekdays) {
            shifts.push(
              ShiftDetail.fromJS({
                startTime: start,
                endTime: end,
                recurrence: ShiftRecurrence.fromJS({
                  $recurrence: "monthlyByDay",
                  weekOfMonth: weekOfMonth,
                  dayOfWeek,
                }),
              }),
            );
          }
          continue;
        }

        if (type === "perStopPerDay") {
          shifts.push(
            ShiftDetail.fromJS({
              startTime: start,
              endTime: end,
              recurrence: ShiftRecurrence.fromJS({
                $recurrence: "perStopPerDay",
                PerStopFrequency: perStopFrequency,
              }),
            }),
          );
        }

        if (type === "perStopPerWeek") {
          shifts.push(
            ShiftDetail.fromJS({
              startTime: start,
              endTime: end,
              recurrence: ShiftRecurrence.fromJS({
                $recurrence: "perStopPerWeek",
                PerStopFrequency: perStopFrequency,
              }),
            }),
          );
        }

        // monthlyByDate
        if (type === "monthlyByDate") {
          shifts.push(
            ShiftDetail.fromJS({
              startTime: start,
              endTime: end,
              recurrence: ShiftRecurrence.fromJS({
                $recurrence: "monthlyByDate",
                dayOfMonth: Number(r.get("dayOfMonth")!.value),
              }),
            }),
          );
        }
      }

      result.push({ siteId, shifts, specialAttention });
    }

    return result;
  }

  public hasMeaningfulShiftDetails(): boolean {
    for (let i = 0; i < this.postsFormArray.length; i++) {
      const rows = this.shiftDetailsFA(i).controls;

      for (const r of rows) {
        const start = (r.get("startTime")?.value as string) || "";
        const end = (r.get("endTime")?.value as string) || "";
        const weekdays = (r.get("weekdays")?.value as number[]) || [];
        const type = r.get("type")?.value as
          | "weekly"
          | "monthlyByDay"
          | "monthlyByDate"
          | undefined;

        if (type === "weekly") {
          if (start || end || (Array.isArray(weekdays) && weekdays.length > 0))
            return true;
        } else if (type === "monthlyByDay") {
          const wom = r.get("weekOfMonth")?.value;
          if (
            start ||
            end ||
            (Array.isArray(weekdays) && weekdays.length > 0) ||
            wom
          )
            return true;
        } else if (type === "monthlyByDate") {
          const dom = r.get("dayOfMonth")?.value;
          if (start || end || dom) return true;
        } else {
          if (start || end) return true;
        }
      }
    }
    return false;
  }

  public applyCoveragePreset(preset: CoveragePreset): void {
    const rows = this.presetToWeeklyRows(preset);
    for (let i = 0; i < this.postsFormArray.length; i++) {
      const formArray = this.shiftDetailsFA(i);
      formArray.clear();
      for (const r of rows) {
        formArray.push(
          this.buildWeeklyRow(r.weekdays, r.start, r.end, r.interval ?? 1),
        );
      }
    }
    this.cdr.markForCheck();
  }

  private presetToWeeklyRows(
    preset: CoveragePreset,
  ): Array<{
    weekdays: number[];
    start: string;
    end: string;
    interval?: number;
  }> {
    switch (preset) {
      case "Mon-Fri":
        return [{ weekdays: [1, 2, 3, 4, 5], start: "09:00", end: "17:00" }];

      case "Mon-Sat":
        return [
          { weekdays: [1, 2, 3, 4, 5], start: "09:00", end: "17:00" }, // Mon–Fri
          { weekdays: [6], start: "09:00", end: "14:00" }, // Sat
        ];

      case "Mon-Sun":
        return [
          { weekdays: [1, 2, 3, 4, 5], start: "09:00", end: "17:00" }, // Mon–Fri
          { weekdays: [6, 0], start: "09:00", end: "14:00" }, // Sat & Sun
        ];

      case "24/7":
        return [
          { weekdays: [0, 1, 2, 3, 4, 5, 6], start: "00:00", end: "23:59" },
        ];
      case "Overnight":
        return [
          { weekdays: [1, 2, 3, 4, 5], start: "17:00", end: "23:59" },
          { weekdays: [2, 3, 4, 5, 6], start: "00:00", end: "08:00" },
        ];
    }
  }

  trackBySiteIdx = (_: number, fg: FormGroup) => fg.get("siteId")?.value ?? _;
  trackByShiftIdx = (idx: number) => idx;

  getSiteId(fg: FormGroup): number | undefined {
    const v = fg.get("siteId")?.value ?? (fg.getRawValue?.() as any)?.siteId;
    const sid = typeof v === "string" ? Number(v) : (v as number);
    return Number.isFinite(sid) ? sid : undefined;
  }

  isPrimarySiteId(siteId: unknown): boolean {
    const sid =
      typeof siteId === "string" ? Number(siteId) : (siteId as number);
    return (
      Number.isFinite(sid) &&
      Number.isFinite(this.primarySiteId) &&
      sid === this.primarySiteId
    );
  }

  get selectableSites(): Site[] {
    const existing = new Set<number>(
      this.postsFormArray.controls
        .map((fg) => fg.get("siteId")?.value as number | null)
        .filter((id): id is number => typeof id === "number"),
    );
    return (this.availableSites ?? []).filter(
      (site) => site?.siteId != null && !existing.has(site.siteId),
    );
  }

  openAddPostDialog(): void {
    if (!this.canModifyPosts) {
      return;
    }
    this.pendingSiteIds = [];
    this.addSiteDialogRef = this.dialog.open(this.addSiteDialogTpl, {
      width: "720px",
      maxWidth: "95vw",
      disableClose: true,
      autoFocus: false,
    });
  }

  cancelAddPosts(): void {
    this.addSiteDialogRef?.close();
  }

  confirmAddPosts(): void {
    const existing = new Set<number>(
      this.postsFormArray.controls
        .map((fg) => fg.get("siteId")?.value as number | null)
        .filter((id): id is number => typeof id === "number"),
    );
    const toAdd = (this.pendingSiteIds ?? []).filter(
      (id) => typeof id === "number" && !existing.has(id),
    );
    const created: ServicePost[] = [];
    for (const siteId of toAdd) {
      this.addPostForSite(siteId);
      created.push({ siteId, shifts: [] } as ServicePost);
    }
    this.pendingSiteIds = [];
    if (created.length > 0) {
      this.postsAdded.emit(created);
    }
    this.addSiteDialogRef?.close();
  }

  private fromApiTime(t: any): string {
    if (!t) return "";
    if (typeof t === "string") return t.length >= 5 ? t.slice(0, 5) : t;
    if (typeof t === "object") {
      if ("ticks" in t && typeof (t as any).ticks === "number") {
        const totalSeconds = Math.floor((t as any).ticks / 10_000_000);
        const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
        const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
          2,
          "0",
        );
        return `${hh}:${mm}`;
      }
      if ("hour" in t && "minute" in t) {
        const hh = String((t as any).hour ?? 0).padStart(2, "0");
        const mm = String((t as any).minute ?? 0).padStart(2, "0");
        return `${hh}:${mm}`;
      }
    }
    return "";
  }

  private toApiTime(v: string): string {
    if (!v) return "";
    const [h, m, s] = v.split(":");
    const hh = String(Number(h) || 0).padStart(2, "0");
    const mm = String(Number(m) || 0).padStart(2, "0");
    const ss = String(Number(s) || 0).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }

  private timeOrderValidator: ValidatorFn = (
    g: AbstractControl,
  ): ValidationErrors | null => {
    const startTimeControl = g.get("startTime");
    const endTimeControl = g.get("endTime");
    if (!startTimeControl || !endTimeControl) return null;

    const startTime = (startTimeControl.value as string) || "";
    const endTime = (endTimeControl.value as string) || "";

    if (!startTime || !endTime) {
      if (endTimeControl.errors?.["timeOrder"]) {
        const { timeOrder, ...rest } = endTimeControl.errors;
        endTimeControl.setErrors(Object.keys(rest).length ? rest : null);
        endTimeControl.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      }
      return null;
    }

    const startTimeMins = this.toMinutes(startTime);
    const endTimeMins = this.toMinutes(endTime);
    const invalid =
      startTimeMins == null ||
      endTimeMins == null ||
      startTimeMins >= endTimeMins;

    if (invalid) {
      const errors = endTimeControl.errors || {};
      if (!errors["timeOrder"]) {
        endTimeControl.setErrors({ ...errors, timeOrder: true });
      }
      endTimeControl.markAsTouched();
      return { timeOrder: true };
    } else {
      if (endTimeControl.errors?.["timeOrder"]) {
        const { timeOrder, ...rest } = endTimeControl.errors;
        endTimeControl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    }
  };

  // accepts "H:MM" or "HH:MM"
  private toMinutes(v: string | null | undefined): number | null {
    if (!v) return null;

    const m = /^(\d{1,2}):(\d{2})/.exec(v);
    if (!m) return null;
    const hh = Math.min(23, Math.max(0, Number(m[1] || 0)));
    const mm = Math.min(59, Math.max(0, Number(m[2] || 0)));
    return hh * 60 + mm;
  }

  private applySpecialAttentionVisibility(): void {
    if (!this.postsFormArray?.length) return;

    for (const fg of this.postsFormArray.controls) {
      const ctrl = fg.get("specialAttention") as FormControl | null;
      if (!ctrl) continue;

      if (this.showSpecialAttention) {
        ctrl.enable({ emitEvent: false });
      } else {
        ctrl.setValue(false, { emitEvent: false });
        ctrl.disable({ emitEvent: false });
      }
    }
    this.cdr.markForCheck();
  }
}
